import { Component, inject, Inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TariffFunctions } from 'src/app/shared/constants/tariff-creation.constants';
import { DisbursmentService } from 'src/app/shared/service/disbursment/disbursment.service';
import { PortService } from 'src/app/shared/service/disbursment/port.service';
import { MaPortService, MaSubService, TariffSubService, TariffService, TariffRequest } from '../create-port-tariff/create-tariff.model';
import { ToastrService } from 'ngx-toastr';
import { AutocompleteOption, TypeaheadService } from './typeahead-service';

@Component({
  selector: 'app-create-tariff',
  templateUrl: './create-tariff.component.html',
  styleUrls: ['./create-tariff.component.scss']
})
export class CreateTariffComponent {
 private loader = inject(DisbursmentService);
 isLoading$ = this.loader.loading$;
  tariffForm: FormGroup;
  isEditMode = false;
  editIndex: number | null = null;

  // Shared help text for input fields
  private readonly inputHelpText = "Valid formats:\n• Vessel property (GRT, LOA, DWT, NRT)\n• Arithmetic expressions (NRT*1.5, NRT+2, NRT-3, NRT/2)\n• Numeric values (1000)\n• Property/Number with tariff service (NRT:Pilotage, 1000:Towage)";

  // Help text for basic value field
  basicValueHelpText = "Valid formats:\n• Vessel property (GRT, LOA, DWT, NRT)\n• Reference subservice\n• Arithmetic expressions (NRT*1.5, NRT+2, NRT-3, NRT/2)\n• Numeric values (1000)\n• Property/Number with tariff service (NRT:Pilotage, 1000:Towage)";

  movementHelpText = "Day and Hour and numeric values are accepted.\nExamples:\n• 1 (for 1 day)\n• 0.5 (for half day)\n• 12 (for 12 hours)\n• 0.25 (for quarter day)";

  // Options for vessel properties (basic value)
  vslProperties: string[] = [];
  filteredVslProperties: string[] = [];

  // Options for tariff services
  tariffServiceOptions: any[] = [];
  filteredTariffServiceOptions: AutocompleteOption[] = [];

  // Options for reference subservices
  filteredReferenceSubServices: string[] = [];

  // Typeahead autocomplete options
  allAutocompleteOptions: AutocompleteOption[] = [];
  filteredAutocompleteOptions: AutocompleteOption[] = [];
  filteredAutocompleteFunctions: AutocompleteOption[] = [];
  filteredAutocompletebasicValueTariffService: AutocompleteOption[] = [];
  filteredAutocompleteResultColumns: AutocompleteOption[] = [];
  filteredAutocompleteOptionsForTariff: AutocompleteOption[] = [];
  filteredAutocompleteFunctionsForTariff: AutocompleteOption[] = [];
  filteredAutocompleteTariffServicesForTariff: AutocompleteOption[] = [];
  filteredAutocompleteResultColumnsForTariff: AutocompleteOption[] = [];
  fileteredAutocompleteOptionsForMovement: AutocompleteOption[] = [];
  filteredAutocompleteFunctionsForMovement: AutocompleteOption[] = [];
  filteredAutocompleteTariffServicesForMovement: AutocompleteOption[] = [];
  filteredAutocompleteResultColumnsForMovement: AutocompleteOption[] = [];
  resultColumns: AutocompleteOption[] = [];


  // Help text for tariff percentage field
  tariffHelpText = this.inputHelpText;

  // Help text for formula result field
  formulaHelpText = "You can:\n• Use any of these terms: Basic Value, Tariff %, Movement\n• Remove any of these terms\n• Change arithmetic operators (+, -, *, /)\n• No other text is allowed";

  purposes: string[] = [];

  // Service and subservice data
  portServices: MaPortService[] = [];
  filteredPortServices: MaPortService[] = [];
  selectedService: MaPortService | null = null;
  filteredSubServices: MaSubService[] = [];
  showTariffServiceColumn: boolean = false;

  private typeaheadService = new TypeaheadService();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateTariffComponent>,
    private snackBar: ToastrService,
    private portService: PortService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {

    // Check if we're in edit mode - consider both direct index and tableData/serviceObj
    this.isEditMode = !!data && (data.index !== undefined || data.tableData || data.serviceObj);
    this.editIndex = data?.index ?? null;
    this.tariffForm = this.fb.group({
      code: [data?.serviceObj?.code || '', Validators.required],
      service: [data?.serviceObj?.service || '', Validators.required],
      purpose: [data?.serviceObj?.purpose || ''],
      info_msg: [data?.serviceObj?.info_msg || ''],
      total: [data?.serviceObj?.total],
      sub_services: this.fb.array([]),
    });
    if (this.isEditMode) {
      this.populateForm(data);
    } else {
      this.addSubService();
      this.buildAutocompleteOptions();

    }
  }

  ngOnInit() {
    if (this.data?.serviceObj) {
      this.tariffForm.patchValue({
        purpose: this.data.serviceObj.purpose || '',
        total: this.data.serviceObj.total || ''
      });
    }


    // Check if master data is passed from parent component
    if (this.data?.masterData) {
      const { vslProperties = [], portServices = [], purposes = [] } = this.data.masterData;
      // Handle vessel_fields as array
      const vesselFieldNames = Array.isArray(this.data?.vessel_fields)
        ? this.data.vessel_fields.map((item: any) => item.field_name || '')
        : [];
      this.vslProperties = [...vslProperties.map((item: any) => item.name || ''), ...vesselFieldNames];

      this.filteredVslProperties = [...this.vslProperties];

      this.portServices = portServices;
      this.filteredPortServices = [...this.portServices];
      this.purposes = purposes.map((item: any) =>
        item.name || ''
      );
      this.tariffServiceOptions = this.data.masterData.tariffServices;
      this.buildAutocompleteOptions();

    } else {
      // Fallback to API calls if master data not provided
      this.loadMasterData();
    }

    // If we're in edit mode, make sure the UI reflects that
    if (this.isEditMode) {
      const dialogTitle = document.querySelector('.dialog-header h6');
      if (dialogTitle) {
        dialogTitle.textContent = 'Edit Tariff';
      }
    }
  }


  loadMasterData() {
    this.loader.showDialogLoader();
    const allowedFields = ["vessel_properties", "port_services", "purpose"];
    const body = { fields: allowedFields };
    this.portService.getMasterData(body).subscribe({
      next: (response) => {
        this.portServices = response.port_services;
        this.filteredPortServices = [...this.portServices];

        this.vslProperties = response.vessel_properties.map((item: any) => item.name || '');
        // Handle vessel_fields as array
        const vesselFields = Array.isArray(this.data?.vessel_fields)
          ? this.data.vessel_fields.map((item: any) => item.field_name || '')
          : [];
        this.filteredVslProperties = [...this.vslProperties, ...vesselFields];

        this.purposes = response.purpose.map((item: any) => item.name || '');

        this.loader.hideDialogLoader();
      },
      error: (error) => {
        this.loader.hideDialogLoader();
        const errorMsg = error.error?.error || 'Something went wrong, Unable to load Suggestions. Please try again.';
        this.snackBar.error(errorMsg);
      }
    });
  }

  //method to filter the services based on the input
  filterServices() {
    const input = this.tariffForm.get('service')?.value?.toLowerCase() || '';
    this.filteredPortServices = this.portServices.filter(service =>
      service.name?.toLowerCase().includes(input) ||
      service.code?.toLowerCase().includes(input)
    );
  }

  onServiceSelected(serviceName: string) {
    if (!serviceName || typeof serviceName !== 'string') return;
    const sanitizedServiceName = serviceName.trim();
    const matched = this.portServices.find(s => s.name === sanitizedServiceName);
    if (matched) {
      this.selectedService = matched;
      this.tariffForm.get('code')?.setValue(matched.code);
      this.filteredSubServices = [...matched.sub_services];

      // Update code in the form but keep the service name
      this.tariffForm.patchValue({
        code: matched.code,
        service: matched.name
      });

      // Build autocomplete options when service is selected
      this.buildAutocompleteOptions();
    } else {
      this.selectedService = null;
      this.filteredSubServices = [];
      this.tariffForm.patchValue({
        service: sanitizedServiceName,
        code: '0'
      });
      this.buildAutocompleteOptions();
    }


  }

  // Handle service field blur - map free entry data
  onServiceBlur() {
    const serviceName = this.tariffForm.get('service')?.value;
    if (serviceName) {
      this.onServiceSelected(serviceName)
    }
  }

  buildAutocompleteOptions() {
    // Get only created services in this port
    const createdServices = this.getTariffRuleServices();

    // Get only created sub-services with names
    const createdSubServices = this.getCreatedSubServices();

    // Extract columns from selected service's tariff services only
    const columns: string[] = [];
    this.selectedService?.tariff_services?.forEach(ts => {
      if (ts.columns && Array.isArray(ts.columns)) {
        columns.push(...ts.columns);
      }
    });
    const existingSubServiceNames = this.subServices.controls
      .map(control => control.value.sub_service).filter(subService => subService);
    this.filteredTariffServiceOptions = this.insertFormatedData(this.tariffServiceOptions?.map((item: any) => item.name || '') || [], 'tariff_column')
    const selectedService = this.selectedService ? this.selectedService.name : this.tariffForm.get('service')?.value || '';
    this.allAutocompleteOptions = this.typeaheadService.buildConditionalAutocompleteOptions(
      this.vslProperties,
      createdServices,
      createdSubServices,
      selectedService || '',
      existingSubServiceNames);
    this.filteredAutocompleteOptions = [...this.allAutocompleteOptions];
    this.filteredAutocompleteOptionsForTariff = [...this.allAutocompleteOptions];
    this.filteredAutocompleteFunctions = TariffFunctions;
    this.filteredAutocompleteFunctionsForTariff = TariffFunctions;
    this.filteredAutocompletebasicValueTariffService = this.filteredTariffServiceOptions;
    this.filteredAutocompleteTariffServicesForTariff = this.filteredTariffServiceOptions;
    this.filteredAutocompleteFunctionsForMovement = TariffFunctions;
    this.filteredAutocompleteTariffServicesForMovement = this.filteredTariffServiceOptions;
    this.fileteredAutocompleteOptionsForMovement = this.insertFormatedData(this.vslProperties, 'vessel_property');

  }

  // method to get the created service for the port
  getTariffRuleServices(): any[] {
    // Only show services that have been created in this port
    if (this.data?.existingServices?.length) {
      const services = this.data.existingServices
        .map((item: any) => item?.serviceObj)
        .filter((serviceObj: any) => serviceObj);

      return services.filter(
        (serviceObj: any, index: number, self: any[]) => {
          return index === self.findIndex((s: any) => s.service === serviceObj.service)
        }
      );
    }
    return [];
  }

  getCreatedSubServices(): any[] {
    const createdSubServices: any[] = [];
    const currentServiceName = this.selectedService?.name || '';

    // Add existing services subservices (from other services only)
    if (this.data?.existingServices) {
      this.data.existingServices.forEach((item: any) => {
        if (item.serviceObj?.sub_services && item.serviceObj.service !== currentServiceName) {
          item.serviceObj.sub_services.forEach((subService: any) => {
            if (subService.name && subService.name.trim()) {
              createdSubServices.push({
                serviceName: item.serviceObj.service,
                subServiceName: subService.name,
                fullName: `${item.serviceObj.service} -> ${subService.name}`
              });
            }
          });
        }
      });
    }

    // Add current session subservices (from other services only)
    const currentFormServices = this.getCurrentFormServices();
    currentFormServices.forEach(service => {
      if (service.serviceName !== currentServiceName) {
        service.subServices.forEach((subService: string) => {
          if (subService.trim()) {
            createdSubServices.push({
              serviceName: service.serviceName,
              subServiceName: subService,
              fullName: `${service.serviceName} -> ${subService}`
            });
          }
        });
      }
    });

    return createdSubServices;
  }

  getServiceSpecificTariffServices(serviceName: string): any[] {
    const service = this.portServices.find(s => s.name === serviceName);
    return service?.tariff_services || [];
  }

  getCurrentFormServices(): any[] {
    const services: any[] = [];
    const formServices = new Map<string, string[]>();

    // Group subservices by service name from current form
    for (let i = 0; i < this.subServices.length; i++) {
      const subServiceGroup = this.subServices.at(i);
      const serviceName = this.selectedService?.name || '';
      const subServiceName = subServiceGroup.get('sub_service')?.value || '';

      if (serviceName && subServiceName.trim()) {
        if (!formServices.has(serviceName)) {
          formServices.set(serviceName, []);
        }
        formServices.get(serviceName)?.push(subServiceName);
      }
    }

    // Convert to array format
    formServices.forEach((subServices, serviceName) => {
      services.push({
        serviceName,
        subServices
      });
    });

    return services;
  }

  onTypeaheadKeydown(event: KeyboardEvent, fieldType: any): void {
    // Show all options after space or separator characters
    if (event.key === ':') {
      event.preventDefault();
    }
    if (event.code === 'Space' || event.key === '(' || event.key === ',' || event.key === '+') {
      setTimeout(() => {
        switch (fieldType) {
          case 'basic_value':
            this.filteredAutocompleteOptions = [...this.allAutocompleteOptions];
            break;
          case 'basic_value_function':
            this.filteredAutocompleteFunctions = [...TariffFunctions];
            break;
          case 'basic_value_tariff_service':
            this.filteredAutocompletebasicValueTariffService = [...this.filteredTariffServiceOptions];
            break;
          case 'basic_value_result_column':
            this.filteredAutocompleteResultColumns = this.resultColumns;
            break;
          case 'tariff':
            this.filteredAutocompleteOptionsForTariff = [...this.allAutocompleteOptions];
            break;
          case 'tariff_function':
            this.filteredAutocompleteFunctionsForTariff = [...TariffFunctions];
            break;
          case 'tariff_service':
            this.filteredAutocompleteTariffServicesForTariff = [...this.filteredTariffServiceOptions];
            break;
          case 'tariff_result_column':
            this.filteredAutocompleteResultColumnsForTariff = this.resultColumns;
            break;
          case 'movement':
            this.fileteredAutocompleteOptionsForMovement = this.insertFormatedData(this.vslProperties, 'vessel_property');
            break;
          case 'movement_function':
            this.filteredAutocompleteFunctionsForMovement = [...TariffFunctions];
            break;
          case 'movement_tariff_service':
            this.filteredAutocompleteTariffServicesForMovement = [...this.filteredTariffServiceOptions];
            break;
          case 'movement_result_column':
            this.filteredAutocompleteResultColumnsForMovement = this.resultColumns;
            break;
        }


      }, 10);
    }
  }

  // Store original values before autocomplete overwrites them
  private originalValues: { [key: string]: string } = {};

  // method to filter the option based on fieldtype and lastword
  onTypeaheadInput(event: Event, fieldType: any): void {
    const target = event.target as HTMLTextAreaElement;
    const text = target.value;

    // Store the current value before autocomplete can overwrite it
    const fieldIndex = this.getFieldIndex(target);
    const fieldKey = `${fieldType}_${fieldIndex}_${Date.now()}`; // Add timestamp to make unique
    this.originalValues[fieldKey] = text;

    // Store the key on the element for retrieval later
    target.setAttribute('data-field-key', fieldKey);
    this.buildAutocompleteOptions();

    // Extract the last words from the text
    const lastWords = this.extractLastWords(text);

    // Create a map for the field types and their corresponding values
    const fieldValues = {
      basic_value: '',
      basic_value_function: '',
      basic_value_tariff_service: '',
      basic_value_result_column: '',
      tariff: '',
      tariff_function: '',
      tariff_service: '',
      tariff_result_column: '',
      movement: '',
      movement_function: '',
      movement_tariff_service: '',
      movement_result_column: ''
    };

    // Assign the appropriate last word to the correct field in the map
    const lastWord = lastWords[lastWords.length - 1] || '';
    if (fieldValues?.hasOwnProperty(fieldType)) {
      (fieldValues as any)[fieldType] = lastWord;
    }
    // If any last word is empty, reset the autocomplete options
    if (this.hasEmptyLastWords(lastWords)) {
      this.resetAutocompleteOptions();
    } else {
      // Filter options for the specific field type
      this.filterOptionsByFieldType(fieldType, (fieldValues as any)[fieldType]);
    }
  }



  // method to filter and assign the option based on field type
  filterOptionsByFieldType(fieldType: string, lastWord: string): void {
    switch (fieldType) {
      case 'basic_value':
        this.filteredAutocompleteOptions = this.filterByLabel(this.allAutocompleteOptions, lastWord);
        break;
      case 'basic_value_function':
        this.filteredAutocompleteFunctions = this.filterByLabel(TariffFunctions, lastWord);
        break;
      case 'basic_value_tariff_service':
        this.filteredAutocompletebasicValueTariffService = this.filterByLabel(this.filteredTariffServiceOptions, lastWord);
        break;
      case 'basic_value_result_column':
        this.filteredAutocompleteResultColumns = this.filterByLabel(this.resultColumns, lastWord);
        break;
      case 'tariff':
        this.filteredAutocompleteOptionsForTariff = this.filterByLabel(this.allAutocompleteOptions, lastWord);
        break;
      case 'tariff_function':
        this.filteredAutocompleteFunctionsForTariff = this.filterByLabel(TariffFunctions, lastWord);
        break;
      case 'tariff_service':
        this.filteredAutocompleteTariffServicesForTariff = this.filterByLabel(this.filteredTariffServiceOptions, lastWord);
        break;
      case 'tariff_result_column':
        this.filteredAutocompleteResultColumnsForTariff = this.filterByLabel(this.resultColumns, lastWord);
        break;
      case 'movement':
        this.fileteredAutocompleteOptionsForMovement = this.filterByLabel(this.insertFormatedData(this.vslProperties, 'vsl properties'), lastWord);
        break;
      case 'movement_function':
        this.filteredAutocompleteFunctionsForMovement = this.filterByLabel(TariffFunctions, lastWord);
        break;
      case 'movement_tariff_service':
        this.filteredAutocompleteTariffServicesForMovement = this.filterByLabel(this.filteredTariffServiceOptions, lastWord);
        break;
      case 'movement_result_column':
        this.filteredAutocompleteResultColumnsForMovement = this.filterByLabel(this.resultColumns, lastWord);
        break;
    }
  }

  // method to check whether the last field is empty or not
  hasEmptyLastWords(lastWords: string[]): boolean {
    if (lastWords.length === 0) {
      return true;
    }
    return lastWords.some(word => !word.trim());
  }

  // method to extract the lastword
  extractLastWords(text: string): string[] {
    const words = text.split(/[\s,()\+]+/);
    return words.map(word => word?.trim()).filter(word => word?.length > 0);
  }

  // Reset autocomplete options to show all if no valid filter is applied
  resetAutocompleteOptions() {
    this.filteredAutocompleteOptions = [...this.allAutocompleteOptions];
    this.filteredAutocompleteFunctions = [...TariffFunctions];
    this.filteredAutocompleteResultColumns = this.resultColumns;
    this.filteredAutocompleteOptionsForTariff = [...this.allAutocompleteOptions];
    this.filteredAutocompleteFunctionsForTariff = [...TariffFunctions];
    this.filteredAutocompleteResultColumnsForTariff = this.resultColumns;
    this.fileteredAutocompleteOptionsForMovement = this.insertFormatedData(this.vslProperties, 'vessel_property');
    this.filteredAutocompleteFunctionsForMovement = [...TariffFunctions];
    this.filteredAutocompleteResultColumnsForMovement = this.resultColumns;
  }

  // Method to filter the options based on the field type
  filterByLabel(options: any[], lastWord: string): any[] {
    return options.filter(option =>
      option.label?.toLowerCase().includes(lastWord.toLowerCase()))
  }



  private getFieldIndex(element: HTMLElement): number {
    const formGroup = element.closest('[formGroupName]');
    return formGroup ? parseInt(formGroup.getAttribute('formGroupName') || '0') : 0;
  }

  onAutocompleteSelected(selectedValue: string, fieldName: string, index: number): void {
    const subServiceGroup = (this.tariffForm.get('sub_services') as FormArray).at(index) as FormGroup;
    this.rangeTypeFunctionality(selectedValue, fieldName, subServiceGroup)
    // Find the input element to get the stored field key
    const inputSelector = `input[formControlName="${fieldName}"]`;  // Change from textarea to input
    const inputs = document.querySelectorAll(inputSelector);
    const input = inputs[index] as HTMLInputElement;  // Cast to HTMLInputElement instead of HTMLTextAreaElement

    let originalValue = '';
    if (input) {
      const fieldKey = input.getAttribute('data-field-key');
      originalValue = fieldKey ? (this.originalValues[fieldKey] || '') : '';

      // Clear the stored value
      if (fieldKey) {
        delete this.originalValues[fieldKey];
        input.removeAttribute('data-field-key');
      }
    }

    // Fallback to current form value if no stored value
    if (!originalValue) {
      originalValue = subServiceGroup.get(fieldName)?.value || '';
    }
    // Use cursor position at end of original text
    const cursorPosition = originalValue.length;

    // Use TypeaheadService method to insert text at cursor
    const result = this.typeaheadService.insertTextAtCursor(
      originalValue,
      cursorPosition,
      selectedValue
    );

    // Set the new value
    subServiceGroup.get(fieldName)?.setValue(result.newValue);


    if (fieldName === 'basic_value_tariff_service') {
      subServiceGroup.get('basic_value_result_column')?.setValue('');
      this.setSelectedTariffServiceColumns(selectedValue, 'filteredAutocompleteResultColumns');
    }
    else if (fieldName === 'tariff_service') {
      subServiceGroup.get('tariff_result_column')?.setValue('');
      this.setSelectedTariffServiceColumns(selectedValue, 'filteredAutocompleteResultColumnsForTariff');
    }
    else if (fieldName === 'movement_tariff_service') {
      subServiceGroup.get('movement_result_column')?.setValue('');
      this.setSelectedTariffServiceColumns(selectedValue, 'filteredAutocompleteResultColumnsForMovement');
    }
  }


  filterSubServices(index: number) {
    if (!this.selectedService) return;

    const subServiceGroup = (this.tariffForm.get('sub_services') as FormArray).at(index) as FormGroup;
    const input = subServiceGroup.get('sub_service')?.value?.toLowerCase() || '';

    this.filteredSubServices = this.selectedService.sub_services.filter(subService =>
      subService.name?.toLowerCase().includes(input)
    );
  }


  isNumeric(value: string): boolean {
    return !isNaN(Number(value)) && value.trim() !== '';
  }

  validateBasicValue(index: number) {
    // No validation - accept any input for basic_value
    const subServiceGroup = (this.tariffForm.get('sub_services') as FormArray).at(index) as FormGroup;
    subServiceGroup.get('basic_value')?.setErrors(null);
  }

  get subServices(): FormArray {
    return this.tariffForm.get('sub_services') as FormArray;
  }

  // Allowed formula text parts
  allowedFormulaTerms = ['Basic Value', 'Movement', 'Tariff %', 'ROE', "1"];
  defaultFormula = 'Basic Value * Movement * Tariff %';

  getValidFormulaTerms(): string[] {
    const customFields = Array.isArray(this.data?.vessel_fields)
      ? this.data.vessel_fields.map((item: any) => item.field_name || '')
      : [];
    return [...this.allowedFormulaTerms, ...customFields];
  }

  validateFormulaInput(event: Event, index: number): void {
    const value = (event.target as HTMLInputElement).value || '';
    const subServiceGroup = (this.tariffForm.get('sub_services') as FormArray).at(index) as FormGroup;

    let remainingText = value;

    // Get all valid terms (system + custom vessel fields)
    const validTerms = [
      ...this.getValidFormulaTerms(),
      ...this.vslProperties
    ];

    // Remove all valid terms safely
    remainingText = this.removeValidTerms(remainingText, validTerms);

    // Remove allowed operators and brackets
    remainingText = remainingText.replace(/[\+\-\*\/\s\{\}\(\)]/g, '');

    const openBraces = (value.match(/\{/g) || []).length;
    const closeBraces = (value.match(/\}/g) || []).length;
    const openParentheses = (value.match(/\(/g) || []).length;
    const closeParentheses = (value.match(/\)/g) || []).length;

    if (openBraces !== closeBraces || openParentheses !== closeParentheses) {
      subServiceGroup.get('formula_result')?.setErrors({ unbalancedBrackets: true });
      return;
    }

    const invalidOperatorPlacement = /(^[\+\-\*\/])|([\+\-\*\/]$)|([\+\-\*\/]{2,})/;

    if (invalidOperatorPlacement.test(value)) {
      subServiceGroup.get('formula_result')?.setErrors({ invalidOperatorPlacement: true });
      return;
    }

    if (remainingText.trim().length > 0) {
      subServiceGroup.get('formula_result')?.setErrors({ invalidFormula: true });
    } else {
      subServiceGroup.get('formula_result')?.setErrors(null);
    }
  }

  private removeValidTerms(text: string, terms: string[]): string {
    let result = text;

    const sortedTerms = [...terms]
      .filter(t => t && t.trim().length > 0)
      .sort((a, b) => b.length - a.length);

    sortedTerms.forEach(term => {
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // Ensures full token match (works with spaces, numbers, underscores, hyphens)
      const regex = new RegExp(`(?<!\\w)${escapedTerm}(?!\\w)`, 'gi');

      result = result.replace(regex, '');
    });

    return result;
  }


  // method to initialize the new subservice
  newSubService(): FormGroup {
    return this.fb.group({
      sub_service: [''],  // Not mandatory
      basic_value: ['', Validators.required],
      basic_value_function: [''],
      basic_value_tariff_service: [''],
      basic_value_result_column: [''],
      tariff: ['', Validators.required],
      tariff_function: [''],
      tariff_service: [''],
      tariff_result_column: [''],
      movement: ['', Validators.required],
      movement_function: [''],
      movement_tariff_service: [''],
      movement_result_column: [''],
      formula_result: [this.defaultFormula],
      optional: [false],
      hide: [false],
      showBaseValueResultColumn: [false],
      showTariffResultColumn: [false],
      showMovementResultColumn: [false],
      showBaseValuTariffServiceColumn: [false],
      showTariffServiceColumn: [false]
    });
  }

  // method to add the new subservice
  addSubService() {
    const newSubService = this.newSubService();
    // Check if any existing subservice is marked as reference
    const hasReference = this.subServices.controls.some(control => control.get('reference')?.value);
    if (hasReference) {
      newSubService.get('reference')?.disable();
    }
    this.subServices.push(newSubService);
    this.buildAutocompleteOptions();

  }

  // method to delete the subservice at the particular index
  removeSubService(index: number) {
    this.subServices.removeAt(index);
  }

  // method to submit the for based 
  onSubmit() {
    if (this.tariffForm.invalid) {
      this.tariffForm.markAllAsTouched();
      this.snackBar.error('Please fill all required fields');
      return;
    }

    const formData = this.tariffForm.getRawValue();
    const serviceName = formData.service;
    const serviceCode = formData.code;

    // Ensure at least one subservice exists
    if (!formData.sub_services || formData.sub_services.length === 0) {
      this.snackBar.error('A service must have at least one subservice');
      return;
    }


    // Validate subservice names and check for duplicates
    const newSubServices = formData.sub_services;

    // Allow any subservice names - no validation against dropdown options

    // Check for duplicates using unique_key property
    if (this.data && !this.isEditMode) {
      const existingServices = this.data.existingServices || [];
      const existingUniqueKeys: string[] = [];

      // Collect all existing unique keys
      existingServices.forEach((item: any) => {
        if (item.serviceObj?.sub_services) {
          item.serviceObj.sub_services.forEach((existingSub: any) => {
            if (existingSub.unique_key) {
              existingUniqueKeys.push(existingSub.unique_key);
            }
          });
        }
      });

      // Check if any current subservice has duplicate unique_key
      for (const subService of newSubServices) {
        const currentUniqueKey = this.generateUniqueKey(serviceName, subService);

        if (existingUniqueKeys.includes(currentUniqueKey)) {
          const subServiceName = subService.sub_service || 'Unnamed';
          this.snackBar.error(`Duplicate subservice detected: '${subServiceName}' already exists`);
          return;
        }
      }
    }
    // Create TariffRequest structure
    const tariffSubServices = formData.sub_services.map((subService: any) => {
      let movement = `${subService.movement_function || "NA"}:` +
        `${subService.movement}:` + `${subService.movement_tariff_service || 'NA'}:` + `${subService.movement_result_column || "NA"}`;

      let basicValue = `${subService.basic_value_function || "NA"}:` +
        `${subService.basic_value || "NA"}:` + `${subService.basic_value_tariff_service || 'NA'}:` +
        `${subService.basic_value_result_column || "NA"}`;

      let tariffPercent = `${subService.tariff_function || "NA"}:` +
        `${subService.tariff || "NA"}:` + `${subService.tariff_service || 'NA'}:` +
        `${subService.tariff_result_column || "NA"}`;

      return new TariffSubService({
        name: subService.sub_service || '',
        basic_value: basicValue,
        tariff_percent: tariffPercent || "1",
        movement: movement || "1",
        formula_result: subService.formula_result,
        optional: subService.optional ? 'Y' : 'N',
        hide: subService.hide ? 'Y' : 'N',
        unique_key: subService.unique_key || this.generateUniqueKey(serviceName, subService)
      });
    });
    // Calculate SNO based on all existing services count (including taxation services)
    const existingServicesCount = this.data?.existingServices ? this.data.existingServices.length : 0;

    const tariffService = new TariffService({
      SNO: existingServicesCount + 1,
      code: serviceCode,
      service: serviceName,
      subService: tariffSubServices,
      purpose: formData.purpose,
      total: formData.total || '',
      info_msg: formData.info_msg
    });
    const tariffRequest = new TariffRequest({
      items: [tariffService]
    });

    // Return TariffRequest with additional metadata for backward compatibility
    const result = {
      tariffRequest,
      isEdit: this.isEditMode,
      index: this.editIndex,
      service: serviceName,
      code: serviceCode,
      sno: existingServicesCount + 1,
      originalServiceIndex: this.data?.originalServiceIndex
    };

    this.dialogRef.close({ data: result });
  }


  populateForm(data: any) {
    // Set the service first
    this.tariffForm.patchValue({
      code: data.code,
      service: data.service,
      purpose: data.purpose,
      total: data.total,
    });

    if (data.serviceObj && data.serviceObj.sub_services && Array.isArray(data.serviceObj.sub_services)) {
      // Clear any existing subservices
      while (this.subServices.length) {
        this.subServices.removeAt(0);
      }
      // Add each subservice from the serviceObj
      data.serviceObj.sub_services.forEach((subService: any) => {
        this.tariffServiceOptions = this.data.masterData.tariffServices || [];
        const [basicValueFunction, extractedBasicValue, basicValueTariffService, basicValueResultColumn] = this.splitAndExtractValuesToDisplay(subService.basic_value);
        const [tariff_function, tariff, tariff_service, tariff_result_column] = this.splitAndExtractValuesToDisplay(subService.tariff_percent);
        const [movementFunction, movement, movementTariffService, movementResultColumn] = this.splitAndExtractValuesToDisplay(subService.movement);

        const subServiceGroup = this.fb.group({
          sub_service: [subService.name || ''],
          basic_value: [extractedBasicValue, Validators.required],
          basic_value_tariff_service: [basicValueTariffService === 'NA' ? '' : basicValueTariffService],
          basic_value_function: [basicValueFunction === 'NA' ? '' : basicValueFunction],
          basic_value_result_column: [basicValueResultColumn === 'NA' ? '' : basicValueResultColumn],
          tariff: [tariff || '', Validators.required],
          tariff_function: [tariff_function === 'NA' ? '' : tariff_function],
          tariff_service: [tariff_service === 'NA' ? '' : tariff_service],
          tariff_result_column: [tariff_result_column === 'NA' ? '' : tariff_result_column],
          movement: [movement || '', Validators.required],
          movement_function: [movementFunction === 'NA' ? '' : movementFunction],
          movement_tariff_service: [movementTariffService === 'NA' ? '' : movementTariffService],
          movement_result_column: [movementResultColumn === 'NA' ? '' : movementResultColumn],
          formula_result: [subService.formula_result || this.defaultFormula],
          optional: [subService.optional === 'Y' || subService.optional === true],
          hide: [subService.hide === 'Y' || subService.hide === true],
          showBaseValueResultColumn: [basicValueFunction === 'Range' ? true : false],
          showTariffResultColumn: [tariff_function === 'Range' ? true : false],
          showMovementResultColumn: [movementFunction === 'Range' ? true : false],
        });

        this.subServices.push(subServiceGroup);
        this.rangeTypeFunctionality(basicValueFunction, 'basic_value_function', subServiceGroup);
        this.rangeTypeFunctionality(basicValueTariffService, 'basic_value_tariff_service', subServiceGroup);
        this.rangeTypeFunctionality(tariff_function, 'tariff_function', subServiceGroup);
        this.rangeTypeFunctionality(tariff_service, 'tariff_service', subServiceGroup);
        this.rangeTypeFunctionality(movementFunction, 'movement_function', subServiceGroup);
        this.rangeTypeFunctionality(movementTariffService, 'movement_tariff_service', subServiceGroup);
        this.setSelectedTariffServiceColumns(basicValueTariffService, 'filteredAutocompleteResultColumns');
        this.setSelectedTariffServiceColumns(tariff_service, 'filteredAutocompleteResultColumnsForTariff');
        this.setSelectedTariffServiceColumns(movementTariffService, 'filteredAutocompleteResultColumnsForMovement');
      });
    } else {
      // Single subservice (normal case)
      this.tariffServiceOptions = this.data.masterData.tariffServices || [];
      const [basicValueFunction, extractedBasicValue, basicValueTariffService, basicValueResultColumn] = this.splitAndExtractValuesToDisplay(data.basic_value);
      const [tariff_function, tariff, tariff_service, tariff_result_column] = this.splitAndExtractValuesToDisplay(data.tariff_percent);
      const [movementFunction, movement, movementTariffService, movementResultColumn] = this.splitAndExtractValuesToDisplay(data.movement);

      const subServiceGroup = this.fb.group({
        sub_service: [data.name || data.subservice || ''],
        basic_value: [extractedBasicValue, Validators.required],
        basic_value_function: [basicValueFunction === 'NA' ? '' : basicValueFunction],
        basic_value_tariff_service: [basicValueTariffService === 'NA' ? '' : basicValueTariffService],
        basic_value_result_column: [basicValueResultColumn === 'NA' ? '' : basicValueResultColumn],
        tariff: [tariff || '', Validators.required],
        tariff_function: [tariff_function === 'NA' ? '' : tariff_function],
        tariff_service: [tariff_service === 'NA' ? '' : tariff_service],
        tariff_result_column: [tariff_result_column === 'NA' ? '' : tariff_result_column],
        movement: [movement || '', Validators.required],
        movement_function: [movementFunction === 'NA' ? '' : movementFunction],
        movement_tariff_service: [movementTariffService === 'NA' ? '' : movementTariffService],
        movement_result_column: [movementResultColumn === 'NA' ? '' : movementResultColumn],
        formula_result: [data.formula_result || data.resultformula || data.result_formula || this.defaultFormula],
        optional: [data.optional === 'Y' || data.optional === true],
        hide: [data.hide === 'Y' || data.hide === true],
        showBaseValueResultColumn: [basicValueFunction === 'Range' ? true : false],
        showTariffResultColumn: [tariff_function === 'Range' ? true : false],
        showMovementResultColumn: [movementFunction === 'Range' ? true : false],
      });
      // Add the subservice to the form array
      this.subServices.push(subServiceGroup);
      this.rangeTypeFunctionality(basicValueFunction, 'basic_value_function', subServiceGroup);
      this.rangeTypeFunctionality(basicValueTariffService, 'basic_value_tariff_service', subServiceGroup);
      this.rangeTypeFunctionality(tariff_function, 'tariff_function', subServiceGroup);
      this.rangeTypeFunctionality(tariff_service, 'tariff_service', subServiceGroup);
      this.rangeTypeFunctionality(movementFunction, 'movement_function', subServiceGroup);
      this.rangeTypeFunctionality(movementTariffService, 'movement_tariff_service', subServiceGroup);
      this.setSelectedTariffServiceColumns(basicValueTariffService, 'filteredAutocompleteResultColumns');
      this.setSelectedTariffServiceColumns(tariff_service, 'filteredAutocompleteResultColumnsForTariff');
      this.setSelectedTariffServiceColumns(movementTariffService, 'filteredAutocompleteResultColumnsForMovement');
    }

    // Make sure vessel properties and port services are loaded before triggering service selection
    const checkDependencies = () => {
      if (this.vslProperties.length > 0 && this.portServices.length > 0) {
        // Both vessel properties and port services are loaded
        // Make sure the service name is set in the form
        const serviceName = data.service || (data.serviceObj ? data.serviceObj.service : '');

        if (serviceName) {
          this.tariffForm.patchValue({ service: serviceName });
          this.onServiceSelected(serviceName);

          // After service is selected, update the filtered sub-services
          setTimeout(() => {
            if (this.selectedService) {
              this.filteredSubServices = [...this.selectedService.sub_services];
            }
          }, 100);
        }
      } else {
        // If dependencies aren't loaded yet, wait and try again
        setTimeout(checkDependencies, 100);
      }
    };

    // Start checking
    setTimeout(checkDependencies, 0);
  }

  closeDialog() {
    this.dialogRef.close({ cancelled: true });
  }

  // method to generate the unique
  private generateUniqueKey(serviceName: string, subService: any): string {
    const parts = [
      this.tariffForm.get('purpose')?.value || '',
      serviceName,
      subService.sub_service || ''
    ];
    return parts.filter(part => !!part && part.trim() !== '').join('|');
  }


  // method to handle the range type
  rangeTypeFunctionality(selectedValue: string, fieldName: string, subServiceGroup: any) {
    const basicValueControl = subServiceGroup.get('basic_value_result_column');
    const basicValueTraiffServiceControl = subServiceGroup.get('basic_value_tariff_service')
    const tariffControl = subServiceGroup.get('tariff_result_column');
    const tariffServiceControl = subServiceGroup.get('tariff_service');
    const movementControl = subServiceGroup.get('movement_result_column');
    const movementTariffServiceControl = subServiceGroup.get('movement_tariff_service');

    if (fieldName === 'basic_value_function') {
      if (selectedValue === 'Range' || selectedValue === 'Bracket' || selectedValue === 'DataSet') {
        subServiceGroup.get('showBaseValueResultColumn')?.setValue(true);
        if (basicValueControl) {
          basicValueControl.setValidators([Validators.required]);
          basicValueControl.updateValueAndValidity();
        }
        if (basicValueTraiffServiceControl) {
          basicValueTraiffServiceControl.setValidators([Validators.required]);
          basicValueTraiffServiceControl.updateValueAndValidity();
        }
      }
      else {
        subServiceGroup.get('showBaseValueResultColumn')?.setValue(false);
        basicValueControl?.clearValidators();
        basicValueControl.updateValueAndValidity();
        basicValueTraiffServiceControl.clearValidators();
        basicValueTraiffServiceControl.updateValueAndValidity();

      }
    }
    if (fieldName === 'tariff_function') {
      if (selectedValue === 'Range' || selectedValue === 'Bracket' || selectedValue === 'DataSet') {
        subServiceGroup.get('showTariffResultColumn')?.setValue(true);
        if (tariffControl) {
          tariffControl.setValidators([Validators.required]);
          tariffControl.updateValueAndValidity();
        }
        if (tariffServiceControl) {
          tariffServiceControl.setValidators([Validators.required]);
          tariffServiceControl.updateValueAndValidity();
        }

      }
      else {
        subServiceGroup.get('showTariffResultColumn')?.setValue(false);
        tariffControl?.clearValidators();
        tariffControl.updateValueAndValidity();
        tariffServiceControl.clearValidators();
        tariffServiceControl.updateValueAndValidity();

      }
    }
    if (fieldName === 'movement_function') {
      if (selectedValue === 'Range' || selectedValue === 'Bracket' || selectedValue === 'DataSet') {
        subServiceGroup.get('showMovementResultColumn')?.setValue(true);
        if (movementControl) {
          movementControl.setValidators([Validators.required]);
          movementControl.updateValueAndValidity();
        }
        if (movementTariffServiceControl) {
          movementTariffServiceControl.setValidators([Validators.required]);
          movementTariffServiceControl.updateValueAndValidity();
        }
      }
      else {
        subServiceGroup.get('showMovementResultColumn')?.setValue(false);
        movementControl?.clearValidators();
        movementControl.updateValueAndValidity();
        movementTariffServiceControl.clearValidators();
        movementTariffServiceControl.updateValueAndValidity();
      }
    }

  }

  // method to insert the data in format
  insertFormatedData(data: any, type: string): AutocompleteOption[] {
    return data?.map((item: any) => ({
      value: item,
      label: item,
      type: type
    })) || [];
  }

  splitAndExtractValuesToDisplay(value: string, defaultValue = ''): string[] {
    const parts = value.split(":");
    return [
      parts[0] || defaultValue,
      parts[1] || defaultValue,
      parts[2] || defaultValue,
      parts[3] || defaultValue,
    ];
  }

  // method to set the result columns based on tariff service selected
  setSelectedTariffServiceColumns(
    selectedValue: string,
    targetField: keyof CreateTariffComponent
  ): void {
    const selectedTariffService = this.tariffServiceOptions.find(
      (tariffService: any) => tariffService?.name === selectedValue
    );

    if (!selectedTariffService) {
      (this[targetField] as AutocompleteOption[]) = [];
      return;
    }
    this.resultColumns = this.insertFormatedData(
      selectedTariffService.columns,
      'tariff_column'
    );

    (this[targetField] as AutocompleteOption[]) = this.insertFormatedData(
      selectedTariffService.columns,
      'tariff_column'
    );
  }
}
